import { Button, Input, Label } from '@sitewise/ui';
import { Eraser } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface Props {
  printedName: string;
  onPrintedNameChange: (name: string) => void;
  readOnly: boolean;
  onCapture: (blob: Blob) => Promise<void>;
  hasSignature: boolean;
  uploading: boolean;
}

export function SignatureField({
  printedName,
  onPrintedNameChange,
  readOnly,
  onCapture,
  hasSignature,
  uploading,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  function pointerPos(e: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  }

  function startDraw(e: React.PointerEvent<HTMLCanvasElement>) {
    if (readOnly) return;
    drawing.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function draw(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || readOnly) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointerPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setDirty(true);
  }

  function endDraw(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    drawing.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }

  function clearCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setDirty(false);
  }

  async function saveSignature() {
    const canvas = canvasRef.current;
    if (!canvas || readOnly) return;
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/png'),
    );
    if (!blob) return;
    await onCapture(blob);
    setDirty(false);
  }

  return (
    <div className="space-y-3 rounded border border-border bg-surface p-3">
      <div className="grid gap-1">
        <Label className="text-xs">Printed name</Label>
        <Input
          value={printedName}
          onChange={(e) => onPrintedNameChange(e.target.value)}
          disabled={readOnly}
          placeholder="Type full name"
        />
      </div>
      <div className="grid gap-1">
        <Label className="text-xs">Signature</Label>
        <canvas
          ref={canvasRef}
          width={480}
          height={120}
          className="h-28 w-full touch-none rounded border border-border bg-white"
          onPointerDown={startDraw}
          onPointerMove={draw}
          onPointerUp={endDraw}
          onPointerLeave={endDraw}
        />
      </div>
      {!readOnly ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={clearCanvas}>
            <Eraser className="h-4 w-4" />
            Clear
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void saveSignature()}
            disabled={uploading || (!dirty && !hasSignature)}
          >
            {uploading ? 'Saving…' : hasSignature ? 'Update signature' : 'Save signature'}
          </Button>
        </div>
      ) : hasSignature ? (
        <p className="text-xs text-fg-muted">Signature on file.</p>
      ) : null}
    </div>
  );
}
